/*
 * Obermaier — product.js
 * <om-product>          variant selection (Section Rendering with ?option_values=, works past 250 variants),
 *                       morphing of [data-pdp-update] regions, quantity rules, add to cart, mobile dock action,
 *                       sticky info column that never hides its own end.
 * <om-gallery>          alt-marker filtering per variant, lead image swap, mobile counter + dots,
 *                       full-screen lightbox (native <dialog>) with swipe, arrow keys and zoom, 3D/AR media.
 * <om-recommendations>  lazy Shopify product recommendations.
 * Original work for Obermaier. No theme-vendor code, no libraries.
 */
import { cart, emit, announce, define, config, parseHTML, initReveal } from '@theme/core';

const desktop = matchMedia('(min-width: 990px)');
const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

function readJSON(el) {
  if (!el) return null;
  try {
    return JSON.parse(el.textContent);
  } catch {
    return null;
  }
}

function fill(template = '', vars = {}) {
  return Object.entries(vars).reduce((s, [k, v]) => s.replaceAll(`__${k}__`, v), template);
}

/* ===================================================================== product */
class OmProduct extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.strings = readJSON(this.querySelector('script[data-pdp-strings]')) || {};
    this.variant = readJSON(this.querySelector('script[data-selected-variant]'));
    this.displayVariant = this.variant;
    this.requiresSize = this.hasAttribute('data-require-size');
    this.sizeSelection = null;
    this.request = null;

    this.onChange = this.onChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onClick = this.onClick.bind(this);
    this.updateDock = this.updateDock.bind(this);
    this.updateSticky = this.updateSticky.bind(this);

    this.addEventListener('change', this.onChange);
    this.addEventListener('submit', this.onSubmit);
    this.addEventListener('click', this.onClick);

    this.syncSelection();
    this.syncStepper();
    this.rememberSelection();
    this.setupThgChoices();
    this.setupDescription();
    this.setupSticky();
    this.setupDock();
  }

  disconnectedCallback() {
    this.request?.abort();
    this.stickyObserver?.disconnect();
    removeEventListener('resize', this.updateSticky);
    this.dockObserver?.disconnect();
    desktop.removeEventListener('change', this.updateDock);
    this.clearDock();
  }

  get form() {
    return document.getElementById(this.dataset.formId);
  }

  get addButton() {
    return this.form?.querySelector('[data-add-button]') || null;
  }

  syncSelection() {
    const sizeField = this.querySelector('[data-option-role="size"]');
    if (this.requiresSize && sizeField) {
      const inputs = [...sizeField.querySelectorAll('input[data-option-value-id]')];
      const chosen = inputs.find((input) => input.dataset.optionValueId === this.sizeSelection);
      if (!chosen) this.sizeSelection = null;
      for (const input of inputs) input.checked = input === chosen;
      const current = sizeField.querySelector('[data-option-current]');
      if (current) current.textContent = chosen?.dataset.value || this.strings.chooseSize;
    }
    const pending = this.requiresSize && !this.sizeSelection;
    this.variant = pending ? null : this.displayVariant;
    this.toggleAttribute('data-size-pending', pending);
    const script = this.querySelector('script[data-selected-variant]');
    if (script) script.textContent = JSON.stringify(this.variant);
    const id = this.form?.querySelector('[data-variant-input]');
    if (id) {
      id.value = this.variant ? String(this.variant.id) : '';
      id.disabled = !this.variant || Boolean(this.request);
    }
    const button = this.addButton;
    if (button) {
      button.disabled = pending || Boolean(this.request) || button.dataset.variantAvailable !== 'true';
      const label = pending ? this.strings.chooseSize : button.dataset.variantLabel;
      const span = button.querySelector('span');
      if (span) span.textContent = label;
      button.dataset.dockLabel = label || '';
      button.dataset.dockPrice = pending ? '' : button.dataset.variantPrice || '';
    }
    const dynamic = this.form?.querySelector('[data-buy-dynamic]');
    if (dynamic) dynamic.hidden = !button || button.disabled;
    this.querySelectorAll('[data-size-price]').forEach((el) => { el.hidden = !pending; });
    this.querySelectorAll('[data-selected-price]').forEach((el) => { el.hidden = pending; });
    if (this.requiresSize) {
      this.querySelectorAll('.om-buy__stock, .om-pdp__b--sku, [data-selected-badge]').forEach((el) => { el.hidden = pending; });
    }
    this.updateDock();
  }

  /* ------------------------------------------------------------- events */
  onChange(event) {
    const input = event.target;
    if (input.matches('input[data-option-value-id]')) {
      this.selectOption(input);
    } else if (input.matches('[data-quantity-input]')) {
      this.clampQuantity(input);
    }
  }

  onClick(event) {
    const step = event.target.closest('[data-step]');
    if (step && this.contains(step)) {
      const input = step.closest('[data-stepper]')?.querySelector('[data-quantity-input]');
      if (!input) return;
      const delta = Number(step.dataset.step) * (Number(input.step) || 1);
      input.value = String((Number(input.value) || 0) + delta);
      this.clampQuantity(input);
      return;
    }

    const descToggle = event.target.closest('[data-desc-toggle]');
    if (descToggle && this.contains(descToggle)) {
      const box = descToggle.closest('[data-desc]');
      if (!box) return;
      const rest = box.querySelector('[data-desc-rest]');
      const open = !box.classList.contains('is-open');
      box.classList.toggle('is-open', open);
      if (rest) rest.hidden = !open;
      // A teaser (no clear first paragraph) gives way to the full text.
      box.querySelector('[data-desc-teaser]')?.toggleAttribute('hidden', open);
      descToggle.setAttribute('aria-expanded', String(open));
      descToggle.querySelector('[data-label-more]').hidden = open;
      descToggle.querySelector('[data-label-less]').hidden = !open;
      if (!open) box.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'nearest' });
      return;
    }

    const more = event.target.closest('[data-open-accordion]');
    if (more) {
      const details = document.getElementById(more.dataset.openAccordion);
      if (!details) return;
      event.preventDefault();
      details.open = true;
      details.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
      details.querySelector('summary')?.focus({ preventScroll: true });
    }
  }

  /* "Mehr lesen" only when there is more: a rest of the description or a first paragraph cut by the clamp. */
  setupDescription() {
    this.querySelectorAll('[data-desc]').forEach((box) => {
      const button = box.querySelector('[data-desc-toggle]');
      const intro = box.querySelector('.om-pdp__intro');
      if (!button || !intro || box.querySelector('[data-desc-rest]')) return;
      requestAnimationFrame(() => { button.hidden = intro.scrollHeight <= intro.clientHeight + 1; });
    });
  }

  setupThgChoices() {
    this.querySelector('[data-thg-separated]')?.remove();
    const combined = this.querySelector('[data-thg-combined]');
    if (combined) combined.hidden = false;
    this.querySelectorAll('[data-thg-execution-ui], [data-thg-marble-ui], [data-thg-lighting-ui]').forEach((el) => el.remove());
    const field = this.querySelector('[data-thg-execution]');
    if (!field) return;
    const entries = [...field.querySelectorAll('input[data-option-value-id]')].map((input) => ({
      input,
      marble: /marmorplatten/i.test(input.dataset.value),
      base: input.dataset.value === 'Für Marmorplatten' ? 'Standard' : input.dataset.value.replace(/ für Marmorplatten$/i, ''),
      available: input.dataset.combo === '1' && input.getAttribute('aria-disabled') !== 'true'
    }));
    const selected = entries.find((entry) => entry.input.checked);
    if (!selected) return;
    const choose = (next, focusId) => {
      if (!next?.available) return;
      next.input.checked = true;
      const fields = [...this.querySelectorAll('[data-variant-picker] fieldset[data-option-position]')]
        .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition));
      const ids = fields.map((el) => el.querySelector('input:checked')?.dataset.optionValueId).filter(Boolean);
      this.renderVariant(ids, focusId);
    };
    const led = entries.find((entry) => entry.base === 'Mit LED-Beleuchtung');
    if (led && combined) {
      const wrapper = document.createElement('fieldset');
      wrapper.dataset.thgLightingUi = '';
      wrapper.className = 'om-thg-lighting';
      const legend = document.createElement('legend');
      legend.className = 'om-variants__legend';
      legend.textContent = field.dataset.lightingLabel;
      const options = document.createElement('div');
      options.className = 'om-thg-spouts__values';
      const currentGrip = combined.querySelector('input:checked');
      const gripImage = currentGrip?.nextElementSibling?.querySelector('img');
      const unlit = entries.find((entry) => entry.base === 'Standard' && entry.marble === selected.marble && entry.available)
        || entries.find((entry) => entry.base === 'Standard' && !entry.marble && entry.available);
      for (const [next, lit] of [[unlit, false], [led, true]]) {
        if (!next) continue;
        const button = document.createElement('button');
        button.type = 'button';
        button.id = this.id + '-lighting-' + (lit ? 'on' : 'off');
        button.className = 'om-thg-spouts__choice om-thg-lighting__choice';
        button.setAttribute('aria-pressed', String((selected.base === 'Mit LED-Beleuchtung') === lit));
        button.disabled = !next.available;
        if (gripImage) {
          const image = gripImage.cloneNode();
          image.alt = '';
          button.append(image);
        }
        const label = document.createElement('span');
        label.textContent = lit ? led.input.dataset.value : field.dataset.unlitLabel;
        button.append(label);
        button.addEventListener('click', () => choose(next, button.id));
        options.append(button);
      }
      wrapper.append(legend, options);
      combined.append(wrapper);
    }
    const bases = [...new Set(entries.filter((entry) => !combined || entry.base !== 'Mit LED-Beleuchtung').map((entry) => entry.base))];
    const hasHigh = bases.some((base) => base.startsWith('Hoher Auslauf'));
    if (bases.length > 1 || hasHigh) {
      const wrapper = document.createElement('fieldset');
      wrapper.dataset.thgExecutionUi = '';
      wrapper.className = 'om-thg-spouts';
      const legend = document.createElement('legend');
      legend.className = 'om-variants__legend';
      legend.textContent = hasHigh ? field.dataset.spoutLabel : field.querySelector('.om-variants__name').textContent;
      const options = document.createElement('div');
      options.className = 'om-thg-spouts__values';
      for (const base of bases) {
        const candidates = entries.filter((entry) => entry.base === base);
        const next = candidates.find((entry) => entry.marble === selected.marble && entry.available)
          || candidates.find((entry) => entry.available) || candidates[0];
        const button = document.createElement('button');
        button.type = 'button';
        button.id = this.id + '-spout-' + next.input.dataset.optionValueId;
        button.className = 'om-thg-spouts__choice';
        button.setAttribute('aria-pressed', String(base === selected.base));
        button.disabled = !next.available;
        const imageSource = candidates.find((entry) => entry.input.dataset.spoutImage)?.input.dataset.spoutImage;
        if (imageSource) {
          const image = document.createElement('img');
          image.src = imageSource;
          image.width = 300;
          image.height = 300;
          image.alt = '';
          button.append(image);
        }
        const label = document.createElement('span');
        label.textContent = hasHigh && base === 'Standard' ? field.dataset.lowLabel : base;
        button.append(label);
        button.addEventListener('click', () => choose(next, button.id));
        options.append(button);
      }
      wrapper.append(legend, options);
      field.before(wrapper);
    }
    if (entries.some((entry) => entry.marble)) {
      const wrapper = document.createElement('div');
      wrapper.dataset.thgMarbleUi = '';
      wrapper.className = 'om-thg-marble';
      const label = document.createElement('label');
      const toggle = document.createElement('input');
      toggle.type = 'checkbox';
      toggle.setAttribute('role', 'switch');
      toggle.id = this.id + '-marble';
      toggle.checked = selected.marble;
      const counterpart = entries.find((entry) => entry.base === selected.base && entry.marble !== selected.marble && entry.available);
      toggle.disabled = !counterpart;
      const track = document.createElement('span');
      track.className = 'om-thg-marble__track';
      track.setAttribute('aria-hidden', 'true');
      const text = document.createElement('span');
      text.textContent = field.dataset.marbleLabel;
      label.append(toggle, track, text);
      wrapper.append(label);
      if (!counterpart && !selected.marble) {
        const hint = document.createElement('p');
        hint.textContent = field.dataset.marbleUnavailable;
        hint.id = this.id + '-marble-hint';
        toggle.setAttribute('aria-describedby', hint.id);
        wrapper.append(hint);
      }
      toggle.addEventListener('change', () => choose(counterpart, toggle.id));
      field.closest('[data-variant-picker]').append(wrapper);
    }
    field.hidden = true;
  }

  /* ---------------------------------------------------- variant change */
  selectOption(input) {
    const fieldsets = [...this.querySelectorAll('[data-variant-picker] fieldset[data-option-position]')]
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition));
    const fieldset = input.closest('fieldset');
    const current = fieldset.querySelector('[data-option-current]');
    if (current) current.textContent = input.dataset.value;

    // Bulk mode: the size is not shown; send the colour with a size available in it (see variant-picker).
    const sizePos = Number(this.dataset.sizePosition) || 0;
    const picker = this.querySelector('[data-variant-picker]');
    if (this.hasAttribute('data-bulk') && sizePos) {
      const byPos = new Map(fieldsets.map((fs) => [Number(fs.dataset.optionPosition), fs.querySelector('input:checked')?.dataset.optionValueId]));
      const sizeId = fieldset.dataset.optionRole === 'color' ? input.dataset.bulkSizeId : null;
      byPos.set(sizePos, sizeId || picker?.dataset.bulkSizeCurrent);
      const ids = [...byPos.keys()].sort((a, b) => a - b).map((pos) => byPos.get(pos));
      this.renderVariant(ids.every(Boolean) ? ids : ids.filter(Boolean), input.id);
      return;
    }

    const partial = input.dataset.combo === '0'
      && (!this.requiresSize || fieldset.dataset.optionRole !== 'size' || Number(this.dataset.colorPosition) === 0);
    if (this.requiresSize) {
      if (fieldset.dataset.optionRole === 'size') this.sizeSelection = input.dataset.optionValueId;
      if (partial && fieldset.dataset.optionRole !== 'size') this.sizeSelection = null;
    }
    const ids = fieldsets
      .filter((fs) => !partial || fs === fieldset)
      .filter((fs) => !this.requiresSize || fs.dataset.optionRole !== 'size' || this.sizeSelection)
      .map((fs) => fs.querySelector('input:checked')?.dataset.optionValueId)
      .filter(Boolean);

    const fallback = partial ? (input.dataset.fallbackIds || '').split(',').filter(Boolean) : [];
    this.renderVariant(fallback.length ? fallback : ids, input.id);
  }

  async renderVariant(ids, focusId) {
    this.request?.abort();
    const request = (this.request = new AbortController());
    const url = location.pathname === '/products_preview'
      ? new URL(location.href)
      : new URL(this.dataset.productUrl, location.origin);
    url.searchParams.delete('variant');
    url.searchParams.set('option_values', ids.join(','));
    url.searchParams.set('section_id', this.sectionId);

    this.classList.add('is-updating');
    this.setAttribute('aria-busy', 'true');
    this.syncSelection();
    try {
      let html;
      try {
        html = await this.fetchWithRetry(url, request.signal, [350]);
      } catch (error) {
        if (error.name === 'AbortError') throw error;
        // Fallback: the full page carries the same section markup (e.g. while the template is re-saved).
        const page = new URL(url);
        page.searchParams.delete('section_id');
        html = await this.fetchWithRetry(page, request.signal, [600]);
      }
      const doc = parseHTML(html);
      if (request.signal.aborted) return;
      this.applyUpdate(doc, focusId);
    } catch (error) {
      if (error.name === 'AbortError' || request.signal.aborted) return;
      this.restoreSelection();
      this.showError(this.strings.variantError);
    } finally {
      if (this.request === request) {
        this.classList.remove('is-updating');
        this.removeAttribute('aria-busy');
        this.request = null;
        this.syncSelection();
      }
    }
  }

  /** GET with short retries for transient errors (network, 404/5xx). */
  async fetchWithRetry(url, signal, delays = [350, 900]) {
    for (let attempt = 0; ; attempt += 1) {
      let response = null;
      try {
        response = await fetch(url, { signal });
      } catch (error) {
        if (error.name === 'AbortError' || attempt >= delays.length) throw error;
      }
      if (response?.ok) return response.text();
      if (response && (response.status < 404 || attempt >= delays.length)) throw new Error(String(response.status));
      await new Promise((resolve) => setTimeout(resolve, delays[attempt]));
      if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
    }
  }

  /** The last confirmed selection, so a failed update never leaves the picker out of step with the price. */
  rememberSelection() {
    this.confirmed = [...this.querySelectorAll('[data-variant-picker] input[data-option-value-id]:checked')].map((input) => input.id);
    this.confirmedSize = this.sizeSelection;
  }

  restoreSelection() {
    this.sizeSelection = this.confirmedSize;
    this.querySelectorAll('[data-variant-picker] input[data-option-value-id]').forEach((input) => { input.checked = false; });
    for (const id of this.confirmed || []) {
      const input = document.getElementById(id);
      if (!input) continue;
      input.checked = true;
      const current = input.closest('fieldset')?.querySelector('[data-option-current]');
      if (current) current.textContent = input.dataset.value;
    }
    this.syncSelection();
    this.setupThgChoices();
  }

  applyUpdate(doc, focusId) {
    const fresh = doc.getElementById(`om-pdp-${this.sectionId}`);
    if (!fresh) throw new Error('Missing product section');

    // 1. Morph the marked regions (price, badges, picker, low stock, quantity rules, SKU).
    this.querySelectorAll('[data-pdp-update]').forEach((region) => {
      const next = fresh.querySelector(`[data-pdp-update="${region.dataset.pdpUpdate}"]`);
      if (next) region.replaceChildren(...next.childNodes);
    });
    this.setupThgChoices();
    if (focusId) document.getElementById(focusId)?.focus({ preventScroll: true });

    // 2. Selected variant (null = no variant for this combination).
    const variant = readJSON(fresh.querySelector('script[data-selected-variant]'));
    this.displayVariant = variant;
    this.variant = variant;
    const script = this.querySelector('script[data-selected-variant]');
    if (script) script.textContent = JSON.stringify(variant);

    // 3. Keep the form (and the dynamic checkout inside it) alive; sync its state.
    const form = this.form;
    if (form) {
      const idInput = form.querySelector('[data-variant-input]');
      if (idInput) {
        idInput.value = variant ? String(variant.id) : '';
        idInput.disabled = !variant;
      }
      const button = form.querySelector('[data-add-button]');
      const nextButton = fresh.querySelector('[data-add-button]');
      if (button && nextButton) {
        button.disabled = nextButton.disabled;
        button.innerHTML = nextButton.innerHTML;
        button.dataset.dockLabel = nextButton.dataset.dockLabel || '';
        button.dataset.dockPrice = nextButton.dataset.dockPrice || '';
        button.dataset.variantAvailable = nextButton.dataset.variantAvailable;
        button.dataset.variantLabel = nextButton.dataset.variantLabel;
        button.dataset.variantPrice = nextButton.dataset.variantPrice;
      }
      const qty = form.querySelector('[data-quantity-input]');
      const nextQty = fresh.querySelector('[data-quantity-input]');
      if (qty && nextQty) {
        for (const attr of ['min', 'max', 'step']) {
          if (nextQty.hasAttribute(attr)) qty.setAttribute(attr, nextQty.getAttribute(attr));
          else qty.removeAttribute(attr);
        }
        this.clampQuantity(qty);
      }
      const dynamic = form.querySelector('[data-buy-dynamic]');
      const nextDynamic = fresh.querySelector('[data-buy-dynamic]');
      if (dynamic && nextDynamic) dynamic.hidden = nextDynamic.hidden;
    }
    this.syncSelection();
    this.rememberSelection();
    form?.querySelector('[data-variant-input]')?.dispatchEvent(new Event('change', { bubbles: true }));
    this.clearError();

    // 4. Deep link.
    const u = new URL(location.href);
    u.searchParams.delete('variant');
    u.searchParams.delete('option_values');
    if (this.variant) {
      u.searchParams.set('variant', this.variant.id);
    } else {
      const selected = [...this.querySelectorAll('[data-variant-picker] input:checked')].map((input) => input.dataset.optionValueId);
      if (selected.length) u.searchParams.set('option_values', selected.join(','));
    }
    history.replaceState(history.state, '', u);

    // 5. Gallery: image set of the chosen values, lead = the variant image.
    const values = [...this.querySelectorAll('[data-variant-picker] input:checked')].map((i) => i.dataset.value);
    if (variant?.options) values.push(...variant.options);
    const gallery = this.querySelector('om-gallery');
    const nextGallery = fresh.querySelector('om-gallery');
    if (gallery?.dataset.variantGallery === 'selected' && nextGallery) {
      gallery.replaceWith(nextGallery);
    } else {
      gallery?.applyVariant?.(variant, values);
    }

    // 6. Tell the theme (bulk sizes, apps) and screen reader users.
    emit('om:variant:change', { sectionId: this.sectionId, productId: Number(this.dataset.productId), variant: this.variant });
    const priceText = this.querySelector('[data-pdp-update^="price"]')?.innerText.replace(/\s+/g, ' ').trim();
    const state = this.addButton?.disabled ? this.addButton.textContent.trim() : '';
    announce([this.variant?.title, priceText, state].filter(Boolean).join(' – '));

    this.updateDock();
  }

  /* ---------------------------------------------------------- quantity */
  clampQuantity(input) {
    const min = Number(input.min) || 1;
    const step = Number(input.step) || 1;
    const max = input.max !== '' && input.hasAttribute('max') ? Number(input.max) : Infinity;
    let value = Number(input.value);
    if (!Number.isFinite(value) || value < min) value = min;
    value = Math.ceil(value / step) * step;
    if (value > max) value = Math.max(min, Math.floor(max / step) * step);
    input.value = String(value);
    this.syncStepper(input);
  }

  syncStepper(input = this.querySelector('[data-quantity-input]')) {
    if (!input) return;
    const stepper = input.closest('[data-stepper]');
    const value = Number(input.value);
    const min = Number(input.min) || 1;
    const max = input.hasAttribute('max') ? Number(input.max) : Infinity;
    const step = Number(input.step) || 1;
    const minus = stepper?.querySelector('[data-step="-1"]');
    const plus = stepper?.querySelector('[data-step="1"]');
    if (minus) minus.disabled = value - step < min;
    if (plus) plus.disabled = value + step > max;
  }

  /* ------------------------------------------------------- add to cart */
  async onSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-product-form]')) return;
    event.preventDefault();
    if (this.request || (this.requiresSize && !this.sizeSelection)) return;

    const button = form.querySelector('[data-add-button]');
    if (!button || button.disabled || button.getAttribute('aria-busy') === 'true') return;

    const data = new FormData(form);
    const id = Number(data.get('id'));
    if (!id) return;
    const qtyInput = form.querySelector('[data-quantity-input]');
    if (qtyInput) this.clampQuantity(qtyInput);
    const item = { id, quantity: Math.max(1, Number(qtyInput?.value || data.get('quantity')) || 1) };
    const properties = {};
    for (const [key, value] of data.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && value !== '') properties[match[1]] = value;
    }
    if (Object.keys(properties).length) item.properties = properties;
    if (data.get('selling_plan')) item.selling_plan = data.get('selling_plan');

    this.setBusy(true);
    this.clearError();
    try {
      await cart.add([item], { source: 'product' });
      const title = this.strings.productTitle || '';
      announce(fill(this.strings.added, { title }));
      if (config.cartType === 'page') location.assign(config.routes.cart || '/cart');
    } catch (error) {
      this.showError(error?.message || this.strings.error);
    } finally {
      this.setBusy(false);
    }
  }

  setBusy(busy) {
    for (const button of [this.addButton, this.dockButton]) {
      if (!button) continue;
      if (busy) button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
    }
  }

  showError(message) {
    const box = this.form?.querySelector('[data-buy-error]');
    if (box) {
      box.textContent = message || this.strings.error || '';
      box.hidden = false;
    } else {
      announce(message || this.strings.error, { tone: 'assertive', visual: true, error: true });
    }
  }

  clearError() {
    const box = this.form?.querySelector('[data-buy-error]');
    if (box) {
      box.hidden = true;
      box.textContent = '';
    }
  }

  /* ------------------------------------------------------ sticky column */
  // A sticky column taller than the viewport would hide its own end. Then it sticks to the bottom
  // edge instead (negative top), so every accordion stays reachable.
  setupSticky() {
    this.info = this.querySelector('.om-pdp__info--sticky');
    if (!this.info || !('ResizeObserver' in window)) return;
    this.stickyObserver = new ResizeObserver(this.updateSticky);
    this.stickyObserver.observe(this.info);
    addEventListener('resize', this.updateSticky, { passive: true });
  }

  updateSticky() {
    if (!this.info) return;
    const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--om-header-h')) || 64;
    const offset = header + 24;
    const height = this.info.offsetHeight;
    const top = height + offset + 24 > innerHeight ? innerHeight - height - 32 : offset;
    this.info.style.setProperty('--om-pdp-sticky-top', `${Math.round(top)}px`);
  }

  /* -------------------------------------------------------- mobile dock */
  // The header's mobile dock (sections/header.liquid) offers a buy action once the page's own button
  // is out of sight: after it scrolled above the viewport, or while it is still below but the gallery
  // has scrolled away (the visitor is reading). Never on first paint, never over the footer.
  setupDock() {
    this.dockSlot = document.querySelector('[data-dock-action]');
    const button = this.addButton;
    if (!this.dockSlot || !button || !('IntersectionObserver' in window)) return;
    this.gallery = this.querySelector('om-gallery');
    // The action steps aside for the site footer (not for the service strip above it).
    const groups = [...document.querySelectorAll('.shopify-section-group-footer-group')];
    this.footers = [document.querySelector('footer.shopify-section-group-footer-group') || groups.at(-1)].filter(Boolean);
    this.dockState = { button: true, buttonAbove: false, gallery: true, footer: new Set() };
    // Only the area between the fixed header and the dock counts as "in view".
    const css = getComputedStyle(document.documentElement);
    const top = Math.round(parseFloat(css.getPropertyValue('--om-header-h')) || 0);
    const bottom = Math.round(parseFloat(css.getPropertyValue('--om-dock-h')) || 0);
    this.dockObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === button) {
          this.dockState.button = entry.isIntersecting;
          this.dockState.buttonAbove = !entry.isIntersecting && entry.boundingClientRect.bottom <= (entry.rootBounds?.top ?? 0);
        } else if (entry.target === this.gallery) {
          this.dockState.gallery = entry.isIntersecting;
        } else if (entry.isIntersecting) {
          this.dockState.footer.add(entry.target);
        } else {
          this.dockState.footer.delete(entry.target);
        }
      }
      this.updateDock();
    }, { rootMargin: `-${top}px 0px -${bottom}px 0px` });
    this.dockObserver.observe(button);
    if (this.gallery) this.dockObserver.observe(this.gallery);
    this.footers.forEach((el) => this.dockObserver.observe(el));
    desktop.addEventListener('change', this.updateDock);
  }

  updateDock() {
    const s = this.dockState;
    if (!s) return;
    // A jump (footer link to the top, anchor, back navigation) can skip the button's own observer
    // callback, so where the button sits is re-measured here instead of trusted from the last entry.
    if (this.addButton && !s.button) {
      const top = Math.round(parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--om-header-h')) || 0);
      s.buttonAbove = this.addButton.getBoundingClientRect().bottom <= top;
    }
    // A sold-out or unavailable selection has nothing to offer in the dock.
    const buyable = this.addButton && !this.addButton.disabled;
    const show = buyable && !desktop.matches && !s.button && (s.buttonAbove || !s.gallery) && s.footer.size === 0;
    if (show) this.renderDockButton(true);
    else this.clearDock();
  }

  renderDockButton(force = false) {
    const slot = this.dockSlot;
    const main = this.addButton;
    if (!slot || !main || (!force && !this.dockButton)) return;
    if (!this.dockButton) {
      this.dockButton = document.createElement('button');
      this.dockButton.type = 'submit';
      this.dockButton.className = 'om-btn om-dock-buy';
      this.dockButton.setAttribute('form', this.dataset.formId);
      this.dockLabel = document.createElement('span');
      this.dockLabel.className = 'om-dock-buy__label';
      this.dockPrice = document.createElement('span');
      this.dockPrice.className = 'om-dock-buy__price';
      this.dockButton.append(this.dockLabel, this.dockPrice);
    }
    const label = main.dataset.dockLabel || main.textContent.trim();
    const price = main.disabled ? '' : main.dataset.dockPrice || '';
    this.dockLabel.textContent = label;
    this.dockPrice.textContent = price;
    this.dockPrice.hidden = !price;
    this.dockButton.setAttribute('aria-label', price ? `${label}, ${price}` : label);
    this.dockButton.disabled = main.disabled;
    if (main.getAttribute('aria-busy') === 'true') this.dockButton.setAttribute('aria-busy', 'true');
    else this.dockButton.removeAttribute('aria-busy');
    if (this.dockButton.parentNode !== slot) slot.replaceChildren(this.dockButton);
    slot.hidden = false;
    slot.closest('.om-dock')?.classList.add('has-action');
    // Long prices on narrow phones: stack label and price instead of cutting either.
    this.dockButton.classList.remove('is-stacked');
    if (this.dockButton.scrollWidth > this.dockButton.clientWidth + 1) this.dockButton.classList.add('is-stacked');
  }

  clearDock() {
    if (!this.dockButton) return;
    const slot = this.dockSlot;
    // Keep keyboard focus on the page when the dock button disappears under it.
    if (document.activeElement === this.dockButton) this.addButton?.focus({ preventScroll: true });
    this.dockButton.remove();
    this.dockButton = null;
    if (slot && !slot.children.length) {
      slot.hidden = true;
      slot.closest('.om-dock')?.classList.remove('has-action');
    }
  }
}

/* ===================================================================== gallery */
class OmGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    if (!this.track) return;
    this.items = [...this.track.children].sort((a, b) => Number(a.dataset.index) - Number(b.dataset.index));
    this.count = this.querySelector('[data-gallery-count]');
    this.dots = this.querySelector('[data-gallery-dots]');
    this.dialog = this.querySelector('[data-lightbox]');
    this.strings = readJSON(this.closest('om-product')?.querySelector('script[data-pdp-strings]')) || {};
    this.index = 0;
    this.lightboxDirty = true;

    // Bring the DOM into the visual order (lead first) so focus order, counter and lightbox agree.
    const lead = this.track.querySelector('.is-lead');
    if (lead && this.track.firstElementChild !== lead) this.track.prepend(lead);

    this.onTrackScroll = this.onTrackScroll.bind(this);
    this.track.addEventListener('scroll', this.onTrackScroll, { passive: true });
    this.addEventListener('click', (event) => this.onClick(event));
    this.setupLightbox();
    this.setupXR();
    this.updateUI();
  }

  get visible() {
    return [...this.track.children].filter((item) => !item.hidden);
  }

  /* --------------------------------------------------- variant filter */
  applyVariant(variant, values) {
    if (!this.track) return;
    const selected = new Set(values.filter(Boolean).map((v) => String(v).trim().toLowerCase()));
    const featured = variant?.featured_media?.id ? String(variant.featured_media.id) : null;
    const anyMatch = this.items.some((item) => item.dataset.marker && selected.has(item.dataset.marker));

    for (const item of this.items) {
      const marker = item.dataset.marker;
      item.hidden = this.dataset.variantGallery === 'selected'
        ? !(item.hasAttribute('data-shared-media') || item.dataset.mediaId === featured)
        : !(!anyMatch || !marker || selected.has(marker) || item.dataset.mediaId === featured);
    }

    const visible = this.items.filter((item) => !item.hidden);
    const lead = visible.find((item) => item.dataset.mediaId === featured) || visible[0];
    this.layout(lead, visible);
  }

  layout(lead, visible) {
    if (!lead) return;
    const ordered = [lead, ...this.items.filter((item) => item !== lead)];
    const changed = ordered.some((item, i) => this.track.children[i] !== item);
    if (changed) this.track.replaceChildren(...ordered);

    this.items.forEach((item) => {
      item.classList.toggle('is-lead', item === lead);
      item.classList.remove('is-wide');
      const card = item.querySelector('.om-gallery__card');
      if (card) card.style.viewTransitionName = item === lead ? `om-product-${this.dataset.productId}` : '';
    });
    const followers = visible.filter((item) => item !== lead);
    if (followers.length % 2 === 1) followers[followers.length - 1].classList.add('is-wide');

    // A mosaic image promoted to lead gets the larger sizes (and a large source) so it stays sharp.
    const leadImg = lead.querySelector('img');
    if (leadImg) {
      leadImg.loading = 'eager';
      const zoom = lead.querySelector('[data-zoom-src]')?.dataset.zoomSrc;
      if (zoom && leadImg.srcset && !/\b(1512|1728|2048|2400)w\b/.test(leadImg.srcset)) leadImg.srcset += `, ${zoom} 2400w`;
      leadImg.sizes = '(min-width: 990px) min(56vw, 1000px), 100vw';
    }

    this.track.scrollTo({ left: 0, behavior: 'instant' });
    this.index = 0;
    this.lightboxDirty = true;
    this.updateUI(true);

    // Desktop: the sticky info column stays in view while the gallery scrolls away. When the new
    // lead image is out of sight, bring the top of the gallery back so the change is visible.
    if (desktop.matches && changed) {
      const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--om-header-h')) || 64;
      if (lead.getBoundingClientRect().bottom < header + 160) {
        const top = scrollY + this.getBoundingClientRect().top - header - 24;
        scrollTo({ top: Math.max(0, top), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      }
    }
  }

  /* -------------------------------------------------- mobile counter */
  onTrackScroll() {
    if (this.scrollFrame) return;
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;
      const width = this.track.clientWidth || 1;
      const index = Math.round(this.track.scrollLeft / width);
      if (index !== this.index) {
        this.index = index;
        this.updateUI();
      }
    });
  }

  updateUI(rebuild = false) {
    const total = this.visible.length;
    const index = Math.min(this.index, Math.max(total - 1, 0));
    const showDots = total > 1 && total <= 8;
    const ui = this.querySelector('.om-gallery__ui');
    if (ui) ui.hidden = total < 2;
    if (this.count) {
      this.count.hidden = showDots;
      this.count.textContent = this.strings.counter ? fill(this.strings.counter, { i: index + 1, n: total }) : `${index + 1} / ${total}`;
    }
    if (!this.dots) return;
    this.dots.hidden = !showDots;
    if (!showDots) return;
    if (rebuild || this.dots.children.length !== total) {
      this.dots.replaceChildren(...Array.from({ length: total }, () => document.createElement('i')));
    }
    [...this.dots.children].forEach((dot, i) => dot.classList.toggle('is-active', i === index));
  }

  /* ----------------------------------------------------------- clicks */
  onClick(event) {
    const opener = event.target.closest('[data-lightbox-open]');
    if (opener && this.dialog) {
      this.openLightbox(opener.closest('.om-gallery__item'), opener);
      return;
    }
    const loadModel = event.target.closest('[data-model-load]');
    if (loadModel) this.loadModel(loadModel.closest('[data-model]'));
  }

  /* --------------------------------------------------------- lightbox */
  setupLightbox() {
    const dialog = this.dialog;
    if (!dialog) return;
    this.lbTrack = dialog.querySelector('[data-lightbox-track]');
    this.lbCount = dialog.querySelector('[data-lightbox-count]');
    this.lbZoom = dialog.querySelector('[data-lightbox-zoom]');
    this.lbPrev = dialog.querySelector('[data-lightbox-step="-1"]');
    this.lbNext = dialog.querySelector('[data-lightbox-step="1"]');
    this.lbIndex = 0;

    dialog.addEventListener('click', (event) => {
      if (event.target.closest('[data-lightbox-close]')) return this.closeLightbox();
      const step = event.target.closest('[data-lightbox-step]');
      if (step) return this.goTo(this.lbIndex + Number(step.dataset.lightboxStep));
      if (event.target.closest('[data-lightbox-zoom]')) return this.toggleZoom();
      // A click on the empty stage (not the image) closes, as people expect from a photo viewer.
      if (event.target.matches('.om-lightbox__pan, .om-lightbox__slide') && !this.zoomed) this.closeLightbox();
    });
    dialog.addEventListener('dblclick', (event) => {
      if (event.target.matches('.om-lightbox__img')) this.toggleZoom(undefined, event);
    });
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') { event.preventDefault(); this.goTo(this.lbIndex + 1); }
      else if (event.key === 'ArrowLeft') { event.preventDefault(); this.goTo(this.lbIndex - 1); }
      else if ((event.key === '+' || event.key === '=') && !this.zoomed) this.toggleZoom(true);
      else if (event.key === '-' && this.zoomed) this.toggleZoom(false);
    });
    dialog.addEventListener('pointermove', (event) => {
      if (!this.zoomed || event.pointerType !== 'mouse') return;
      const slide = this.currentSlide();
      if (!slide) return;
      const r = slide.getBoundingClientRect();
      slide.scrollLeft = ((event.clientX - r.left) / r.width) * (slide.scrollWidth - slide.clientWidth);
      slide.scrollTop = ((event.clientY - r.top) / r.height) * (slide.scrollHeight - slide.clientHeight);
    });
    this.lbTrack.addEventListener('scroll', () => {
      if (this.lbFrame) return;
      this.lbFrame = requestAnimationFrame(() => {
        this.lbFrame = null;
        const index = Math.round(this.lbTrack.scrollLeft / (this.lbTrack.clientWidth || 1));
        if (index !== this.lbIndex) {
          if (this.zoomed) this.toggleZoom(false);
          this.lbIndex = index;
          this.updateLightboxUI();
        }
      });
    }, { passive: true });
    dialog.addEventListener('close', () => {
      if (this.zoomed) this.toggleZoom(false);
      document.documentElement.classList.remove('om-locked');
      if (this.lbOpener && document.contains(this.lbOpener)) this.lbOpener.focus({ preventScroll: true });
      // Keep the page gallery in step with the last image viewed.
      const item = this.lbItems?.[this.lbIndex];
      if (item && !desktop.matches) {
        const i = this.visible.indexOf(item);
        if (i >= 0) this.track.scrollTo({ left: i * this.track.clientWidth, behavior: 'instant' });
      }
    });
  }

  buildSlides() {
    this.lbItems = this.visible.filter((item) => item.dataset.type === 'image');
    const slides = this.lbItems.map((item) => {
      const source = item.querySelector('img');
      const li = document.createElement('div');
      li.className = 'om-lightbox__slide';
      li.setAttribute('role', 'group');
      li.setAttribute('aria-roledescription', 'slide');
      const pan = document.createElement('div');
      pan.className = 'om-lightbox__pan';
      const img = document.createElement('img');
      img.className = 'om-lightbox__img';
      img.alt = source?.alt || '';
      img.decoding = 'async';
      img.loading = 'lazy';
      img.draggable = false;
      const zoom = item.querySelector('[data-zoom-src]')?.dataset.zoomSrc;
      if (source?.srcset) img.srcset = zoom ? `${source.srcset}, ${zoom} 2400w` : source.srcset;
      img.sizes = '100vw';
      img.src = source?.src || '';
      if (source?.width && source?.height) {
        img.width = source.width;
        img.height = source.height;
      }
      pan.append(img);
      li.append(pan);
      return li;
    });
    this.lbTrack.replaceChildren(...slides);
    const total = slides.length;
    slides.forEach((slide, i) => slide.setAttribute('aria-label', fill(this.strings.slide, { i: i + 1, n: total })));
    this.dialog.classList.toggle('is-single', total < 2);
    this.lightboxDirty = false;
  }

  openLightbox(item, opener) {
    if (this.lightboxDirty) this.buildSlides();
    const index = Math.max(0, this.lbItems.indexOf(item));
    const slide = this.lbTrack.children[index];
    const img = slide?.querySelector('img');
    if (img) img.loading = 'eager';
    this.lbOpener = opener;
    document.documentElement.classList.add('om-locked');
    this.dialog.showModal();
    this.lbIndex = index;
    this.lbTrack.scrollTo({ left: index * this.lbTrack.clientWidth, behavior: 'instant' });
    this.updateLightboxUI();
  }

  closeLightbox() {
    if (this.dialog?.open) this.dialog.close();
  }

  currentSlide() {
    return this.lbTrack?.children[this.lbIndex] || null;
  }

  goTo(index) {
    const total = this.lbTrack.children.length;
    if (index < 0 || index >= total) return;
    if (this.zoomed) this.toggleZoom(false);
    const img = this.lbTrack.children[index].querySelector('img');
    if (img) img.loading = 'eager';
    this.lbTrack.scrollTo({ left: index * this.lbTrack.clientWidth, behavior: reducedMotion.matches ? 'instant' : 'smooth' });
  }

  updateLightboxUI() {
    const total = this.lbTrack.children.length;
    if (this.lbCount) this.lbCount.textContent = fill(this.strings.counter, { i: this.lbIndex + 1, n: total });
    if (this.lbPrev) this.lbPrev.disabled = this.lbIndex <= 0;
    if (this.lbNext) this.lbNext.disabled = this.lbIndex >= total - 1;
    // Warm up the neighbours so swiping never shows an empty stage.
    [this.lbIndex - 1, this.lbIndex + 1].forEach((i) => {
      const img = this.lbTrack.children[i]?.querySelector('img');
      if (img) img.loading = 'eager';
    });
  }

  toggleZoom(force, pointer) {
    const slide = this.currentSlide();
    const img = slide?.querySelector('img');
    if (!slide || !img) return;
    const zoom = force ?? !this.zoomed;
    if (zoom) {
      const rect = img.getBoundingClientRect();
      const natural = img.naturalWidth || rect.width * 2.5;
      const width = Math.max(rect.width * 1.6, Math.min(rect.width * 2.5, natural));
      img.style.width = `${Math.round(width)}px`;
      img.style.height = 'auto';
      slide.classList.add('is-zoomed');
      this.dialog.classList.add('is-zoomed');
      const fx = pointer ? (pointer.clientX - rect.left) / rect.width : 0.5;
      const fy = pointer ? (pointer.clientY - rect.top) / rect.height : 0.5;
      requestAnimationFrame(() => {
        slide.scrollLeft = fx * (slide.scrollWidth - slide.clientWidth);
        slide.scrollTop = fy * (slide.scrollHeight - slide.clientHeight);
      });
    } else {
      slide.scrollTo(0, 0);
      img.style.width = '';
      img.style.height = '';
      slide.classList.remove('is-zoomed');
      this.dialog.classList.remove('is-zoomed');
    }
    this.zoomed = zoom;
    if (this.lbZoom) {
      this.lbZoom.setAttribute('aria-pressed', String(zoom));
      this.lbZoom.setAttribute('aria-label', zoom ? this.strings.zoomOut : this.strings.zoomIn);
    }
  }

  /* -------------------------------------------------------- 3D and AR */
  loadModel(container) {
    if (!container || container.classList.contains('is-loaded')) return;
    const template = container.querySelector('template[data-model-template]');
    if (!template) return;
    container.append(template.content.cloneNode(true));
    container.classList.add('is-loaded');
    const viewer = container.querySelector('model-viewer');
    const shopify = window.Shopify;
    if (viewer && shopify?.loadFeatures) {
      shopify.loadFeatures([{
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: (errors) => {
          if (errors || !window.Shopify?.ModelViewerUI) return;
          const ui = new window.Shopify.ModelViewerUI(viewer);
          ui.play?.();
        },
      }]);
    }
    viewer?.focus?.({ preventScroll: true });
  }

  setupXR() {
    const models = readJSON(this.querySelector('script[data-product-models]'));
    const shopify = window.Shopify;
    if (!models?.length || !shopify?.loadFeatures) return;
    shopify.loadFeatures([{
      name: 'shopify-xr',
      version: '1.0',
      onLoad: (errors) => {
        if (errors) return;
        const init = () => {
          window.ShopifyXR.addModels(models);
          window.ShopifyXR.setupXRElements();
        };
        if (window.ShopifyXR) init();
        else document.addEventListener('shopify_xr_initialized', init, { once: true });
      },
    }]);
  }

  get zoomed() {
    return this._zoomed === true;
  }

  set zoomed(value) {
    this._zoomed = value;
  }
}

/* ============================================================ recommendations */
class OmRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.dataset.loaded || !this.dataset.url) return;
    // Load right after the page instead of when the rail comes near the viewport: inserted while the
    // visitor is still above it, the rail does not push visible content (CLS on short product pages).
    const start = () => this.load();
    if ('requestIdleCallback' in window) requestIdleCallback(start, { timeout: 1200 });
    else setTimeout(start, 200);
  }

  async fetchMarkup(url) {
    const response = await fetch(url);
    if (!response.ok) throw new Error(String(response.status));
    const doc = parseHTML(await response.text());
    return doc.querySelector('om-recommendations');
  }

  async load() {
    this.dataset.loaded = 'loading';
    // The section is addressed by its template id; the section file name is the fallback (e.g. while a
    // template is being re-saved). Spaced retries cover transient errors (404 bursts of several seconds
    // happen while theme files sync); the rail is below the fold, so waiting costs nothing.
    const primary = new URL(this.dataset.url, location.origin);
    const fallback = new URL(primary);
    if (this.dataset.sectionFile) fallback.searchParams.set('section_id', this.dataset.sectionFile);
    const attempts = [primary, fallback, primary, fallback, primary, fallback];
    const delays = [600, 2500, 6000, 10000, 15000];
    for (let i = 0; i < attempts.length; i += 1) {
      try {
        const fresh = await this.fetchMarkup(attempts[i]);
        if (fresh && fresh.children.length) {
          this.replaceChildren(...fresh.childNodes);
          // Cards rely on their own module; load it once the markup is in place.
          import('@theme/product-card').catch(() => {});
          initReveal(this);
        }
        this.dataset.loaded = 'true';
        return;
      } catch {
        if (i < attempts.length - 1) await new Promise((resolve) => setTimeout(resolve, delays[i]));
      }
    }
    this.dataset.loaded = 'error';
  }
}

define('om-product', OmProduct);
define('om-gallery', OmGallery);
define('om-recommendations', OmRecommendations);
